import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    UsersIcon,
    CubeIcon,
    ArrowPathRoundedSquareIcon,
    WrenchScrewdriverIcon,
    BoltIcon,
    ShieldCheckIcon,
    SparklesIcon,
    ArrowLongRightIcon,
    ArrowLongLeftIcon
} from '@heroicons/react/24/outline';
import { iconToSlug } from '../../data/servicesData';
import { useFirestore } from '../../hooks/useFirestore';
import { getServices } from '../../firebase/firestore';
import Section from '../common/Section';
import Card from '../common/Card';
import Loading from '../common/Loading';
import './ServicesPreview.css';

const ServicesPreview = () => {
    const { t, i18n } = useTranslation();
    const { data: services, loading } = useFirestore(useCallback(() => getServices(), []));

    const getServiceIcon = (service) => {
        const iconMap = {
            'elevator': UsersIcon,
            'truck': CubeIcon,
            'stairs': ArrowPathRoundedSquareIcon,
            'walk': BoltIcon, // Representing moving walks/motion
            'wrench': WrenchScrewdriverIcon,
            'refresh': ShieldCheckIcon // Representing safety/reliability in modernization
        };
        const IconComponent = iconMap[service.icon] || SparklesIcon;
        return <IconComponent className="card-icon-inner" />;
    };

    if (loading) {
        return (
            <Section className="services-preview">
                <Loading message={t('common.loading')} />
            </Section>
        );
    }

    const previewServices = services ? services.slice(0, 6) : [];

    return (
        <Section
            className="services-preview"
            id="services"
        >
            <div className="section-title text-center">
                <h2>{t('services.title')}</h2>
                <div className="accent-line center"></div>
                <p>{t('services.subtitle')}</p>
            </div>

            <div className="grid grid-3">
                {previewServices.map((service, index) => {
                    const isAr = i18n.language === 'ar';
                    const ArrowIcon = isAr ? ArrowLongLeftIcon : ArrowLongRightIcon;
                    const slug = iconToSlug[service.icon];
                    return (
                        <motion.div
                            key={service.id}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1, duration: 0.5 }}
                        >
                            <Card hover={true}>
                                <div className="card-icon">
                                    {getServiceIcon(service)}
                                </div>
                                <h3>{isAr ? service.titleAr : service.titleEn}</h3>
                                <p>{isAr ? service.descAr : service.descEn}</p>
                                {slug && (
                                    <Link
                                        to={`/services/${slug}`}
                                        className="service-card-link"
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: 'var(--primary-gold)', fontWeight: 600, marginTop: '1rem', fontSize: '0.95rem' }}
                                    >
                                        {isAr ? 'اعرف المزيد' : 'Learn More'}
                                        <ArrowIcon style={{ width: '1.1rem' }} />
                                    </Link>
                                )}
                            </Card>
                        </motion.div>
                    );
                })}
            </div>

            <div className="services-more">
                <Link to="/services" className="btn btn-ghost">
                    {t('services.viewAll')}
                    {i18n.language === 'ar' ? <ChevronLeftIcon className="btn-icon" /> : <ChevronRightIcon className="btn-icon" />}
                </Link>
            </div>
        </Section>
    );
};

export default ServicesPreview;
